import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Divider,
  Chip,
  Paper,
  Fade,
  Tooltip,
  IconButton
} from "@mui/material";
import { Search, Megaphone, ClipboardList } from "lucide-react";
import banner from "../General/banner.png";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  pearl: "#EEF3F4",
  aqua: "#00C0CA",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  red: "#F65F4E"
};

const DashboardComunicacion = () => {
  const navigate = useNavigate();
  const [programas, setProgramas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState("2026");

  useEffect(() => {
    if (localStorage.getItem("rol") !== "comunicacion") {
      navigate("/");
      localStorage.clear();
    }
  }, [navigate]);

  useEffect(() => {
    const fetchProgramas = async () => {
      try {
        let response = await fetch(`${apiUrl}/Programa/Get`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'apiKey': localStorage.getItem("apiKey")
          }
        });
        if (response.ok) {
          let data = await response.json();
          setProgramas(data.sort((a, b) => a.nombre.localeCompare(b.nombre)));
        }
      } catch (error) {
        console.error('Error en la solicitud', error);
      }
    };
    fetchProgramas();
  }, [apiUrl]);

  const extraerAnio = (nombre) => {
    const match = nombre.match(/\d{4}/);
    return match ? match[0] : "S/A";
  };

  const añosDisponibles = ["Todos", ...new Set(programas.map(p => extraerAnio(p.nombre)))].sort().reverse();

  const filteredProgramas = programas.filter((p) => {
    const cumpleNombre = p.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const cumpleAnio = selectedYear === "Todos" || extraerAnio(p.nombre) === selectedYear;
    return cumpleNombre && cumpleAnio;
  });

  return (
    <Box sx={{ p: { xs: 2, md: 6 }, pt: { xs: 10, md: 14 }, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      
      {/* BANNER */}
      <Paper elevation={0} sx={{ 
        position: 'relative', mb: 6, borderRadius: '32px', overflow: 'hidden', 
        height: { xs: 180, md: 240 },
        border: `1px solid ${PALETTE.sky}`
      }}>
        <Box component="img" src={banner} alt="Banner" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <Box sx={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          background: `linear-gradient(90deg, ${PALETTE.dark}E6 0%, ${PALETTE.dark}40 100%)`,
          display: 'flex', flexDirection: 'column', justifyContent: 'center', p: { xs: 3, md: 6 }
        }}>
          <Typography variant="overline" sx={{ color: PALETTE.ice, fontWeight: 800, letterSpacing: 3 }}>
            ÁREA DE COMUNICACIÓN
          </Typography>
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 900, fontSize: { xs: '2rem', md: '3.5rem' }, mb: 1 }}>
            Gestión de Pedidos <Megaphone size={32} color={PALETTE.aqua} style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '10px' }} />
          </Typography>
          <Typography variant="h6" sx={{ color: 'white', opacity: 0.8, fontWeight: 400 }}>
            Hola {localStorage.getItem("nombre")}
          </Typography>
        </Box>
      </Paper>

      {/* FILA DE BÚSQUEDA Y ACCIÓN GLOBAL */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: 2, 
        mb: 8,
        flexDirection: { xs: 'column', md: 'row' } 
      }}>
        
        {/* BUSCADOR PÍLDORA */}
        <Paper 
          elevation={0} 
          sx={{ 
            display: 'flex', alignItems: 'center', px: 3, py: 1, 
            borderRadius: '50px', border: `2px solid ${PALETTE.ice}`,
            backgroundColor: '#fff', width: { xs: '100%', md: '600px' },
            boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
            transition: '0.3s',
            '&:focus-within': { borderColor: PALETTE.aqua }
          }}
        >
          <Search size={22} color={PALETTE.teal} />
          <TextField
            fullWidth
            variant="standard"
            placeholder="Buscar programa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { px: 2, fontSize: '1rem', color: PALETTE.dark, fontWeight: 500 } }}
          />
          <Divider sx={{ height: 30, mx: 2 }} orientation="vertical" />
          <FormControl variant="standard" sx={{ minWidth: 100 }}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disableUnderline
              sx={{ fontSize: '0.9rem', fontWeight: 700, color: PALETTE.teal }}
            >
              {añosDisponibles.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* BOTÓN "TODOS LOS PEDIDOS" COMPACTO */}
        <Tooltip title="Ver todos los pedidos globales">
          <Paper
            elevation={0}
            onClick={() => navigate(`/pedidos`)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.8,
              borderRadius: '50px', backgroundColor: PALETTE.dark,
              cursor: 'pointer', transition: '0.3s',
              border: `2px solid ${PALETTE.dark}`,
              '&:hover': {
                backgroundColor: 'white',
                '& .text-btn': { color: PALETTE.dark },
                '& .icon-btn': { color: PALETTE.dark }
              }
            }}
          >
            <ClipboardList className="icon-btn" size={20} color="white" />
            <Typography className="text-btn" variant="button" sx={{ color: 'white', fontWeight: 800, whiteSpace: 'nowrap' }}>
              Todos los Pedidos
            </Typography>
          </Paper>
        </Tooltip>
      </Box>

      {/* GRID DE PROGRAMAS */}
      <Typography variant="h5" sx={{ fontWeight: 800, color: PALETTE.dark, mb: 4, textAlign: 'center' }}>
        Programas por Año
      </Typography>

      <Grid container spacing={3}>
        {filteredProgramas.map((item) => (
          <Grid item xs={6} sm={4} md={3} key={item.id}>
            <Fade in={true}>
              <Card
                elevation={0}
                onClick={() => navigate(`/listaPedidosPorPrograma/${item.id}`)}
                sx={{
                  height: '100%', p: 3, cursor: "pointer", borderRadius: '28px',
                  backgroundColor: 'white', border: `1px solid #f0f0f0`,
                  transition: "0.3s",
                  "&:hover": { 
                      borderColor: PALETTE.sky, 
                      transform: "translateY(-8px)", 
                      boxShadow: '0 15px 30px rgba(0,0,0,0.05)' 
                  },
                }}
              >
                <Box sx={{ 
                    height: 110, mb: 2, display: 'flex', alignItems: 'center', 
                    justifyContent: 'center', backgroundColor: '#fafafa', 
                    borderRadius: '20px', p: 2 
                }}>
                  <img
                    src={`data:${item.logoMimeType};base64,${item.logo}`}
                    alt={item.nombre}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: 'contain' }}
                  />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: PALETTE.dark, textAlign: 'center' }}>
                  {item.nombre.replace(/\d{4}/, "").trim()}
                </Typography>
                <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                  <Chip 
                    label={extraerAnio(item.nombre)} 
                    size="small" 
                    sx={{ bgcolor: PALETTE.ice, color: PALETTE.azur, fontWeight: 700 }} 
                  />
                </Box>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardComunicacion;